export const focusableElementList = 'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])';

export const focusTrap = (element, event) => {
  const focusableElements = [...element.querySelectorAll(focusableElementList)].filter((el) => !el.hasAttribute('disabled'));
  if (event.key === 'Tab' && document.activeElement === focusableElements[event.shiftKey ? 0 : focusableElements.length - 1]) {
    focusableElements[event.shiftKey ? focusableElements.length - 1 : 0].focus();
    event.preventDefault();
  }
};

/**
 * Makes a floating UI element auto-focusable and auto-closable on focus loss
 * Useful for popups, modals, dropdowns, menus, and other floating UI elements
 * Automatically maintains tab order by restoring focus to where the user tabbed to
 *
 * @param {string} elementSelector - CSS selector for the element to focus (e.g., '.popup.visible .content')
 * @param {Function} onClose - Callback to close the element when focus leaves
 * @returns {void}
 *
 * @example
 * // In Popup's onMount callback
 * <Popup
 *   onMount={() => {
 *     makeAutoFocusable('.my-popup.visible .popup-box', () => this.closePopup());
 *   }}
 * />
 *
 * @example
 * // In Modal's onMount
 * <Modal
 *   onMount={() => {
 *     makeAutoFocusable('.my-modal.visible .modal-content', () => this.closeModal());
 *   }}
 * />
 */
export const makeAutoFocusable = (elementSelector, onClose) => {
  requestAnimationFrame(() => {
    const element = document.querySelector(elementSelector);
    if (element) {
      // Make element focusable if it is not already
      if (!element.hasAttribute('tabindex')) {
        element.setAttribute('tabindex', '-1');
      }

      // Focus the element
      element.focus();

      // Close when focus leaves the element
      const handleFocusOut = () => {
        // Use setTimeout to allow browser to update document.activeElement
        setTimeout(() => {
          // Check if focus has moved outside the element
          if (!element.contains(document.activeElement)) {
            onClose();
            element.removeEventListener('focusout', handleFocusOut);
          }
        }, 0);
      };

      element.addEventListener('focusout', handleFocusOut);
    }
  });
};

/** Unfocus the current `activeElement` being focused on. */
export const unfocus = () => {
  document?.activeElement?.blur?.();
};
